import React, { useMemo, useState } from "react";
import Alert from "../../components/common/Alert";
import Card from "../../components/common/Card";
import FilterBar, {
  formatDateInputValue,
} from "../../components/common/FilterBar";
import PageHeader from "../../components/common/PageHeader";
import { StatusDonutChart } from "../../components/common/ReportingCharts";
import {
  useGetBusinessesQuery,
  useGetPaymentSummaryQuery,
} from "../../store/api";
import {
  formatCurrency,
  getDefaultReportingFilters,
  getPresetDateRange,
  getStoreOptionsFromBusinesses,
  getUtcRangeFromDateInputs,
} from "../../utils/reporting";

const PaymentSummary = () => {
  const { data: businesses } = useGetBusinessesQuery();
  const storeOptions = useMemo(
    () => getStoreOptionsFromBusinesses(businesses),
    [businesses],
  );
  const [draftFilters, setDraftFilters] = useState(
    getDefaultReportingFilters(),
  );
  const [appliedFilters, setAppliedFilters] = useState(
    getDefaultReportingFilters(),
  );

  const queryArgs = useMemo(() => {
    const range = getUtcRangeFromDateInputs(appliedFilters);
    return {
      storeIds: appliedFilters.storeIds,
      from: range.from,
      to: range.to,
    };
  }, [appliedFilters]);

  const { data, isLoading, isFetching, isError, error } =
    useGetPaymentSummaryQuery(queryArgs);

  const currencyCode = data?.currencyCode || "USD";
  const datePresets = [
    { value: "today", label: "Today" },
    { value: "last7", label: "Last 7 Days" },
    { value: "last30", label: "Last 30 Days" },
  ];
  const activePreset =
    datePresets.find((preset) => {
      const range = getPresetDateRange(preset.value);
      return draftFilters.from === range.from && draftFilters.to === range.to;
    })?.value || "";
  const segments = [
    {
      label: "Captured",
      value: Number(data?.capturedCount || 0),
      color: "#10b981",
    },
    {
      label: "Partial",
      value: Number(data?.partialCount || 0),
      color: "#f59e0b",
    },
    {
      label: "Unpaid",
      value: Number(data?.unpaidCount || 0),
      color: "#94a3b8",
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payment Summary"
        subtitle="Payment completion distribution for completed orders only."
      />

      <Card>
        <div className="mb-6">
          <FilterBar
            startDate={draftFilters.from}
            endDate={draftFilters.to}
            onStartDateChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                from: value ? formatDateInputValue(value) : "",
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            onEndDateChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                to: value ? formatDateInputValue(value) : "",
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            stores={storeOptions}
            storeId={draftFilters.storeIds[0] || ""}
            onStoreChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                storeIds: value ? [value] : [],
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            presets={datePresets}
            activePreset={activePreset}
            onPresetChange={(presetKey) => {
              const range = getPresetDateRange(presetKey);
              const nextFilters = {
                ...draftFilters,
                from: range.from,
                to: range.to,
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            onReset={() => {
              const next = getDefaultReportingFilters();
              setDraftFilters(next);
              setAppliedFilters(next);
            }}
            isLoading={isFetching}
          />
        </div>

        {isError && (
          <Alert tone="error">
            {error?.data?.detail || "Failed to load payment summary."}
          </Alert>
        )}

        {isLoading ? (
          <div className="h-[320px] animate-pulse rounded-xl bg-slate-100" />
        ) : (
          <div className="space-y-6">
            <div className="border-t border-slate-100 pt-6">
              <div className="mb-5">
                <h2 className="text-lg font-semibold text-slate-900">
                  Payment Status Mix
                </h2>
                <p className="text-sm text-slate-500">
                  Historical distribution for completed orders. Not a live
                  payment operations dashboard.
                </p>
              </div>
              <StatusDonutChart
                segments={segments}
                total={Number(data?.ordersCount || 0)}
              />
              <div className="mt-6 border-t border-slate-100 pt-4 text-sm text-slate-600">
                Net sales:{" "}
                <strong className="text-slate-900">
                  {formatCurrency(data?.netSales, currencyCode)}
                </strong>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PaymentSummary;
